import { useState } from 'react';
import { z } from 'zod';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Circle, Loader2, CalendarDays, X, Info, Check, RotateCcw } from 'lucide-react';
import { format, parseISO, differenceInDays } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { CaseMilestone } from '@/hooks/useCaseMilestones';
import { MilestoneIcon } from '@/utils/milestoneIcons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  STATUS_CATEGORY_COLORS,
  getStatusCategory,
  type StatusCategory,
} from '@/utils/statusCategoryMap';
import { getDecisionConfig, isUscisPetitionMilestone } from './decisionConfig';

type StageField = {
  key: string;
  label: string;
  type: 'text' | 'date' | 'select';
  options?: string[];
  mask?: string;
  currency?: boolean;
};

type StageStatusFieldConfig = {
  visibleFields: StageField[];
  requiredKeys: string[];
};

export type StatusUpdateExtra = {
  data?: Record<string, any>;
  completed_date?: string | null;
  status_category?: string;
  /** Temporário: limpa completamente os dados da etapa (botão de reset) */
  resetData?: boolean;
};

interface StageCardProps {
  milestone: CaseMilestone;
  index: number;
  onToggleComplete: (milestone: CaseMilestone) => Promise<void>;
  onUpdateStatus?: (id: string, status: string, extra?: StatusUpdateExtra) => Promise<void>;
  isUpdating: boolean;
  /** Negociação cancelada: etapa congelada (somente leitura) */
  isLocked?: boolean;
}

/* ── Portuguese labels for granular statuses ── */
const STATUS_LABELS_PT: Record<string, string> = {
  pending: 'Pendente',
  waiting: 'Aguardando',
  collecting: 'Coletando',
  preparing: 'Preparando',
  recruitment: 'Recrutamento',
  quiet_period: 'Per. Silencioso',
  clock_paused: 'Relógio Pausado',
  sent: 'Enviado',
  filed: 'Protocolado',
  submitted: 'Protocolado',
  scheduled: 'Agendado',
  clock_running: 'Relógio Rodando',
  swa_posted: 'Publicado SWA',
  ads_published: 'Anúncios Publicados',
  recruiting: 'Recrutando',
  eligible: 'Elegível',
  card_produced: 'Cartão Produzido',
  card_mailed: 'Cartão Enviado',
  current: 'Atual',
  rfi: 'RFI',
  rfi_received: 'RFI Recebido',
  missed: 'Perdido',
  completed: 'Concluído',
  approved: 'Aprovado',
  certified: 'Certificado',
  receipt_received: 'Recibo Recebido',
  card_received: 'Recebido',
  received: 'Recebido',
  rfi_responded: 'RFI Respondido',
  rfe_received: 'RFE Recebido',
  rfe_responded: 'RFE Respondido',
  noid_received: 'NOID Recebido',
  noid_responded: 'NOID Respondido',
  i485_filed: 'I-485 Protocolado',
  audit: 'Auditoria',
  denied: 'Negado',
  decision: 'Decisão',
  created: 'Conta Criada',
  in_progress: 'Em Andamento',
  requested: 'Solicitado',
  bona_fide: 'Bona Fide',
  waitlisted: 'Na Lista de Espera',
  not_applicable: 'Não se Aplica',
  visa_obtained: 'Visto Obtido',
  entered: 'Entrou nos EUA',
  issued: 'Emitido',
  decided: 'Decidido',
  paid: 'Pago',
  confirmed: 'Confirmado',
  partial: 'Parcial',
  hearing_scheduled: 'Audiência Agendada',
  order_obtained: 'Ordem Obtida',
  passport_submitted: 'Passaporte Entregue',
};

const DATE_VALUE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const EMPTY_FIELD_CONFIG: StageStatusFieldConfig = { visibleFields: [], requiredKeys: [] };

function translateStatus(status: string): string {
  return STATUS_LABELS_PT[status] ?? status.replace(/_/g, ' ');
}

/** Status that finalizes a stage (green) — carries an editable completion date. */
function isCompletionStatus(status: string): boolean {
  const cat = getStatusCategory(status);
  return cat === 'completed' || cat === 'failed';
}

/**
 * Mutually exclusive outcomes: choosing one must clear the other.
 * Ex.: Aprovado x Negado, Concluído x Perdido.
 */
const EXCLUSIVE_STATUS_GROUPS: string[][] = [
  ['approved', 'denied'],
  ['completed', 'missed'],
  ['card_received', 'denied'],
  ['received', 'denied'],
  ['bona_fide', 'denied'],
  ['certified', 'denied'],
];

function getExclusiveSiblings(status: string): string[] {
  const siblings = new Set<string>();
  EXCLUSIVE_STATUS_GROUPS.filter((g) => g.includes(status)).forEach((g) =>
    g.forEach((s) => { if (s !== status) siblings.add(s); })
  );
  return Array.from(siblings);
}

// Milestone type helpers
function isPwdMilestoneName(name: string) {
  return name?.toLowerCase().startsWith('pwd');
}
function isPermMilestoneName(name: string) {
  return name?.toLowerCase().startsWith('perm');
}
/**
 * Toda petição de imigração (USCIS) usa o mesmo padrão do I-140:
 * preparação → protocolo → Decisão (Aprovado / Negado / RFE / NOID).
 */
function hasPetitionDecision(name: string) {
  return isUscisPetitionMilestone(name || '');
}


function getFieldOrFallback(
  fields: StageField[],
  key: string,
  label: string,
  type: StageField['type'] = 'text'
): StageField {
  return fields.find((field) => field.key === key) ?? { key, label, type };
}

/* ── Custom date input showing pt-BR format (dd/MMM/yyyy) ── */
function DateInputBR({
  value,
  onChange,
  clearable,
}: {
  value: string;
  onChange: (val: string) => void;
  clearable?: boolean;
}) {
  const display = value
    ? format(parseISO(value), 'dd/MMM/yyyy', { locale: ptBR })
    : '';
  return (
    <div className="relative w-full">
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full text-sm border border-border rounded-md px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary h-[34px]"
      />
      {value && (
        <div className="absolute inset-0 rounded-md bg-background flex items-center px-2 text-sm text-foreground pointer-events-none select-none">
          {display}
          {clearable && (
            <button
              type="button"
              onClick={() => onChange('')}
              className="ml-auto pointer-events-auto text-muted-foreground hover:text-foreground text-base leading-none px-1"
              title="Limpar data"
            >
              ×
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Currency helpers ── */
function formatCurrency(raw: string): string {
  const cleaned = raw.replace(/[^\d.]/g, '');
  if (!cleaned) return '';

  const parts = cleaned.split('.');
  const formattedDollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const decimalPart = parts.length > 1 ? '.' + parts[1].slice(0, 2) : '';
  return '$' + formattedDollars + decimalPart;
}

function CurrencyInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (val: string) => void;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(formatCurrency(e.target.value))}
      onBlur={(e) => {
        const val = e.target.value;
        if (val && !val.includes('.')) onChange(val + '.00');
      }}
      placeholder="$0.00"
      className="w-full text-sm border border-border rounded-md px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary font-mono"
    />
  );
}

/* ── Mask helpers ── */
function applyMask(raw: string, mask: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 0) return '';

  let result = '';
  let digitIndex = 0;

  for (let i = 0; i < mask.length; i++) {
    const ch = mask[i];
    if (ch === '#') {
      if (digitIndex >= digits.length) break;
      result += digits[digitIndex++];
    } else if (/[A-Za-z]/.test(ch)) {
      result += ch;
    } else {
      // Separator (-, .) — only add if there are more digits to follow
      const hasMoreDigits = mask.slice(i + 1).includes('#') && digitIndex < digits.length;
      if (hasMoreDigits) result += ch;
      else break;
    }
  }

  return result;
}

function MaskedTextInput({
  mask,
  value,
  onChange,
}: {
  mask: string;
  value: string;
  onChange: (val: string) => void;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(applyMask(e.target.value, mask))}
      placeholder={mask.replace(/#/g, '_')}
      className="w-full text-sm border border-border rounded-md px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary font-mono"
    />
  );
}

/* ── Dual date helper ── */
function DualDate({ dateStr }: { dateStr: string | null }) {
  if (!dateStr) return <span>—</span>;
  try {
    const date = parseISO(dateStr);
    const br = format(date, 'dd/MMM/yyyy', { locale: ptBR });
    const us = format(date, 'MM/dd/yyyy', { locale: enUS });
    return (
      <span className="inline-flex flex-col leading-tight">
        <span className="font-mono text-xs font-medium text-foreground">{br}</span>
        <span className="font-mono text-[9px] text-muted-foreground">US: {us}</span>
      </span>
    );
  } catch {
    return <span>{dateStr}</span>;
  }
}

/* ── Left border color by category ── */
const BORDER_LEFT_COLORS: Record<StatusCategory, string> = {
  completed: 'before:bg-emerald-500',
  internal_action: 'before:bg-amber-400',
  external_wait: 'before:bg-blue-400',
  action_required: 'before:bg-rose-500',
  not_started: 'before:bg-border',
  failed: 'before:bg-red-600',
};

/* ── Icon background by category ── */
const ICON_BG: Record<StatusCategory, string> = {
  completed: 'bg-emerald-50 dark:bg-emerald-900/30',
  internal_action: 'bg-amber-50 dark:bg-amber-900/30',
  external_wait: 'bg-blue-50 dark:bg-blue-900/30',
  action_required: 'bg-rose-50 dark:bg-rose-900/30',
  not_started: 'bg-muted',
  failed: 'bg-red-50 dark:bg-red-900/30',
};

const STATUS_DATE_REQUIRED_KEYS: Record<string, string[]> = {
  filed: ['filing_date'],
  submitted: ['submission_date', 'submitted_date'],
  rfe_received: ['rfe_date'],
  rfe_responded: ['rfe_response_date'],
  noid_received: ['noid_date'],
  noid_responded: ['noid_response_date'],
  rfi_received: ['rfi_date'],
  rfi_responded: ['rfi_response_date'],
  card_received: ['card_received_date'],
  receipt_received: ['receipt_date'],
  i485_filed: ['i485_filing_date'],
};

// PWD_DECISION_STATUSES removed — now uses getDecisionConfig()

function getStatusFieldConfig(
  milestoneName: string,
  status: string,
  fields: StageField[],
  data?: Record<string, any>
): StageStatusFieldConfig {
  const dateFields = fields.filter((field) => field.type === 'date');
  const byKey = (key: string) => dateFields.find((field) => field.key === key);
  const byAnyKey = (key: string) => fields.find((field) => field.key === key);

  // RFE/NOID: mesmas três datas em QUALQUER formulário de imigração —
  // data que recebeu, prazo de resposta e data que respondeu.
  if (status === 'rfe_received' || status === 'rfe_responded') {
    return {
      visibleFields: [
        getFieldOrFallback(fields, 'rfe_date', 'Data que Recebeu RFE', 'date'),
        getFieldOrFallback(fields, 'rfe_deadline_date', 'Prazo de Resposta', 'date'),
        getFieldOrFallback(fields, 'rfe_response_date', 'Data que Respondeu', 'date'),
      ],
      requiredKeys: status === 'rfe_received' ? ['rfe_date'] : ['rfe_response_date'],
    };
  }
  if (status === 'noid_received' || status === 'noid_responded') {
    return {
      visibleFields: [
        getFieldOrFallback(fields, 'noid_date', 'Data que Recebeu NOID', 'date'),
        getFieldOrFallback(fields, 'noid_deadline_date', 'Prazo de Resposta', 'date'),
        getFieldOrFallback(fields, 'noid_response_date', 'Data que Respondeu', 'date'),
      ],
      requiredKeys: status === 'noid_received' ? ['noid_date'] : ['noid_response_date'],
    };
  }



  if (milestoneName === 'Envio (I-589)') {
    if (status === 'sent') {
      const sent =
        byKey('sent_date') ??
        byKey('filing_date') ??
        ({ key: 'sent_date', label: 'Data de Envio', type: 'date' } as StageField);
      return { visibleFields: [sent], requiredKeys: [sent.key] };
    }

    if (status === 'receipt_received') {
      const official =
        byKey('official_filing_date') ??
        byKey('receipt_date') ??
        ({ key: 'official_filing_date', label: 'Data Oficial de Filing (USCIS)', type: 'date' } as StageField);
      const notice =
        byKey('notice_received_date') ??
        ({ key: 'notice_received_date', label: 'Data Receb. I-797C (Escritório)', type: 'date' } as StageField);
      return {
        visibleFields: [official, notice],
        requiredKeys: [official.key, notice.key],
      };
    }

    return EMPTY_FIELD_CONFIG;
  }

  if (milestoneName === 'Biometria' && status === 'scheduled') {
    const appointmentDate =
      byAnyKey('appointment_date') ??
      ({ key: 'appointment_date', label: 'Data Agendada', type: 'date' } as StageField);
    const location =
      byAnyKey('location') ??
      ({ key: 'location', label: 'Endereço', type: 'text' } as StageField);
    const appointmentTime =
      byAnyKey('appointment_time') ??
      ({ key: 'appointment_time', label: 'Horário', type: 'text' } as StageField);

    return {
      visibleFields: [appointmentDate, location, appointmentTime],
      requiredKeys: [appointmentDate.key, location.key, appointmentTime.key],
    };
  }

  if (isPwdMilestoneName(milestoneName)) {
    if (status === 'preparing') {
      const hasStarted = !!data?.preparation_start_date;
      if (!hasStarted) {
        return {
          visibleFields: [
            getFieldOrFallback(fields, 'preparation_start_date', 'Data de Início', 'date'),
          ],
          requiredKeys: ['preparation_start_date'],
        };
      } else {
        return {
          visibleFields: [
            getFieldOrFallback(fields, 'preparation_completed_date', 'Data de Finalização', 'date'),
          ],
          requiredKeys: [],
        };
      }
    }
    if (status === 'submitted') {
      return {
        visibleFields: [
          { ...getFieldOrFallback(fields, 'case_number', 'Nº do Caso'), mask: 'P-###-#####-######' },
          { ...getFieldOrFallback(fields, 'wage_level', 'Nível Salarial'), currency: true },
          { ...getFieldOrFallback(fields, 'soc_code', 'Código SOC'), mask: '##-####.##' },
          getFieldOrFallback(fields, 'filing_date', 'Data do Protocolo', 'date'),
        ],
        requiredKeys: ['filing_date'],
      };
    }
    if (status === 'rfi') {
      return {
        visibleFields: [
          getFieldOrFallback(fields, 'rfi_received_date', 'Data que Recebeu', 'date'),
          getFieldOrFallback(fields, 'rfi_due_date', 'Prazo para Responder', 'date'),
          getFieldOrFallback(fields, 'rfi_response_date', 'Data que Respondeu', 'date'),
        ],
        requiredKeys: ['rfi_received_date', 'rfi_due_date'],
      };
    }
    if (status === 'approved') {
      return {
        visibleFields: [getFieldOrFallback(fields, 'validity_date', 'Validade', 'date')],
        requiredKeys: [],
      };
    }
    return EMPTY_FIELD_CONFIG;
  }

  if (isPermMilestoneName(milestoneName)) {
    if (status === 'preparing') {
      const hasStarted = !!data?.preparation_start_date;
      if (!hasStarted) {
        return {
          visibleFields: [
            getFieldOrFallback(fields, 'preparation_start_date', 'Data de Início', 'date'),
          ],
          requiredKeys: ['preparation_start_date'],
        };
      } else {
        return {
          visibleFields: [
            getFieldOrFallback(fields, 'preparation_completed_date', 'Data de Finalização', 'date'),
          ],
          requiredKeys: [],
        };
      }
    }
    if (status === 'submitted') {
      return {
        visibleFields: [
          { ...getFieldOrFallback(fields, 'case_number', 'Nº do Caso PERM'), mask: 'G-###-#####-######' },
          getFieldOrFallback(fields, 'filing_date', 'Data Filing', 'date'),
        ],
        requiredKeys: ['filing_date'],
      };
    }
    if (status === 'rfi') {
      return {
        visibleFields: [
          getFieldOrFallback(fields, 'rfi_received_date', 'Data que Recebeu', 'date'),
          getFieldOrFallback(fields, 'rfi_due_date', 'Prazo para Responder', 'date'),
          getFieldOrFallback(fields, 'rfi_response_date', 'Data que Respondeu', 'date'),
        ],
        requiredKeys: ['rfi_received_date', 'rfi_due_date'],
      };
    }
    if (status === 'audit') {
      return {
        visibleFields: [getFieldOrFallback(fields, 'audit_date', 'Data da Auditoria', 'date')],
        requiredKeys: ['audit_date'],
      };
    }
    return EMPTY_FIELD_CONFIG;
  }

  if (hasPetitionDecision(milestoneName)) {
    if (status === 'preparing') {
      const hasStarted = !!data?.preparation_start_date;
      if (!hasStarted) {
        return {
          visibleFields: [
            getFieldOrFallback(fields, 'preparation_start_date', 'Data de Início', 'date'),
          ],
          requiredKeys: ['preparation_start_date'],
        };
      } else {
        return {
          visibleFields: [
            getFieldOrFallback(fields, 'preparation_completed_date', 'Data de Finalização', 'date'),
          ],
          requiredKeys: [],
        };
      }
    }
    if (status === 'submitted' || status === 'filed') {
      return {
        visibleFields: [
          getFieldOrFallback(fields, 'case_number', 'Nº do Caso'),
          getFieldOrFallback(fields, 'receipt_number', 'Nº do Recibo'),
          getFieldOrFallback(fields, 'filing_date', 'Data Filing', 'date'),
        ],
        requiredKeys: ['filing_date'],
      };
    }
    if (status === 'rfe_received') {
      return {
        visibleFields: [
          getFieldOrFallback(fields, 'rfe_date', 'Data que Recebeu RFE', 'date'),
          getFieldOrFallback(fields, 'rfe_deadline_date', 'Prazo de Resposta', 'date'),
          getFieldOrFallback(fields, 'rfe_response_date', 'Data que Respondeu', 'date'),
        ],
        requiredKeys: ['rfe_date'],
      };
    }
    if (status === 'rfe_responded') {
      return {
        visibleFields: [getFieldOrFallback(fields, 'rfe_response_date', 'Data Resposta RFE', 'date')],
        requiredKeys: ['rfe_response_date'],
      };
    }
    if (status === 'noid_received') {
      return {
        visibleFields: [
          getFieldOrFallback(fields, 'noid_date', 'Data que Recebeu NOID', 'date'),
          getFieldOrFallback(fields, 'noid_deadline_date', 'Prazo de Resposta', 'date'),
          getFieldOrFallback(fields, 'noid_response_date', 'Data que Respondeu', 'date'),
        ],
        requiredKeys: ['noid_date'],
      };
    }
    if (status === 'noid_responded') {
      return {
        visibleFields: [getFieldOrFallback(fields, 'noid_response_date', 'Data Resposta NOID', 'date')],
        requiredKeys: ['noid_response_date'],
      };
    }
    if (status === 'approved') {
      return {
        visibleFields: [getFieldOrFallback(fields, 'approval_date', 'Data Aprovação', 'date')],
        requiredKeys: [],
      };
    }
    if (status === 'denied') {
      return {
        visibleFields: [getFieldOrFallback(fields, 'denial_date', 'Data da Negativa', 'date')],
        requiredKeys: [],
      };
    }
  }

  if (status === 'collecting') {
    return {
      visibleFields: [getFieldOrFallback(fields, 'collecting_date', 'Data de Coleta', 'date')],
      requiredKeys: ['collecting_date'],
    };
  }

  const requiredKeys = STATUS_DATE_REQUIRED_KEYS[status] ?? [];
  const visibleFields = requiredKeys.map((key) => byKey(key)).filter(Boolean) as StageField[];

  // Qualquer etapa protocolada pode registrar nº do caso/recibo (opcional).
  if (status === 'filed' || status === 'submitted') {
    const filingDate =
      visibleFields.find((f) => f.key === 'filing_date') ??
      getFieldOrFallback(fields, 'filing_date', 'Data do Protocolo', 'date');
    return {
      visibleFields: [
        getFieldOrFallback(fields, 'receipt_number', 'Nº do Recibo / Processo'),
        filingDate,
      ],
      requiredKeys: [],
    };
  }

  return {
    visibleFields,
    requiredKeys: visibleFields.map((field) => field.key),
  };
}

/**
 * Every status must be datable. When the stage has no specific field for the
 * chosen status, offer a generic (optional) date so the team can register/adjust
 * when it actually happened.
 */
function resolveStatusFieldConfig(
  milestoneName: string,
  status: string,
  fields: StageField[],
  data?: Record<string, any>
): StageStatusFieldConfig {
  const config = getStatusFieldConfig(milestoneName, status, fields, data);
  if (config.visibleFields.length > 0 || isCompletionStatus(status)) return config;

  const genericKey = `${status}_date`;
  return {
    visibleFields: [getFieldOrFallback(fields, genericKey, 'Data', 'date')],
    requiredKeys: [],
  };
}

function getDisplayFieldsForCurrentStatus(
  milestoneName: string,
  status: string,
  fields: StageField[],
  data: Record<string, any>
) {
  const buildDisplay = (
    baseFields: Array<{ key: string; label: string; type: 'text' | 'date' }>,
    extraByStatus: Record<string, Array<{ key: string; label: string; type: 'text' | 'date' }>>
  ) => {
    const allFields = [...baseFields, ...(extraByStatus[status] ?? [])];
    return allFields
      .map((f) => getFieldOrFallback(fields, f.key, f.label, f.type))
      ;
  };

  // RFE/NOID sempre exibem as três datas, em qualquer processo.
  if (status === 'rfe_received' || status === 'rfe_responded') {
    return [
      getFieldOrFallback(fields, 'rfe_date', 'Data que Recebeu RFE', 'date'),
      getFieldOrFallback(fields, 'rfe_deadline_date', 'Prazo de Resposta', 'date'),
      getFieldOrFallback(fields, 'rfe_response_date', 'Data que Respondeu', 'date'),
    ];
  }
  if (status === 'noid_received' || status === 'noid_responded') {
    return [
      getFieldOrFallback(fields, 'noid_date', 'Data que Recebeu NOID', 'date'),
      getFieldOrFallback(fields, 'noid_deadline_date', 'Prazo de Resposta', 'date'),
      getFieldOrFallback(fields, 'noid_response_date', 'Data que Respondeu', 'date'),
    ];
  }



  if (isPwdMilestoneName(milestoneName)) {
    return buildDisplay(
      [
        { key: 'case_number', label: 'Nº do Caso', type: 'text' },
        { key: 'wage_level', label: 'Nível Salarial', type: 'text' },
        { key: 'soc_code', label: 'Código SOC', type: 'text' },
      ],
      {
        rfi: [
          { key: 'rfi_received_date', label: 'Data que Recebeu', type: 'date' },
          { key: 'rfi_due_date', label: 'Prazo para Responder', type: 'date' },
          { key: 'rfi_response_date', label: 'Data que Respondeu', type: 'date' },
        ],
        approved: [{ key: 'validity_date', label: 'Validade', type: 'date' }],
      }
    );
  }

  if (isPermMilestoneName(milestoneName)) {
    return buildDisplay(
      [
        { key: 'case_number', label: 'Nº do Caso PERM', type: 'text' },
        { key: 'filing_date', label: 'Data Filing', type: 'date' },
      ],
      {
        audit: [{ key: 'audit_date', label: 'Data da Auditoria', type: 'date' }],
        rfi: [
          { key: 'rfi_received_date', label: 'Data que Recebeu', type: 'date' },
          { key: 'rfi_due_date', label: 'Prazo para Responder', type: 'date' },
          { key: 'rfi_response_date', label: 'Data que Respondeu', type: 'date' },
        ],
      }
    );
  }

  if (hasPetitionDecision(milestoneName)) {
    return buildDisplay(
      [
        { key: 'case_number', label: 'Nº do Caso', type: 'text' },
        { key: 'receipt_number', label: 'Nº do Recibo', type: 'text' },
        { key: 'preparation_start_date', label: 'Início Preparação', type: 'date' },
        { key: 'preparation_completed_date', label: 'Fim Preparação', type: 'date' },
        { key: 'filing_date', label: 'Data Filing', type: 'date' },
      ],
      {
        rfe_received: [
          { key: 'rfe_date', label: 'Data que Recebeu RFE', type: 'date' },
          { key: 'rfe_deadline_date', label: 'Prazo de Resposta', type: 'date' },
          { key: 'rfe_response_date', label: 'Data que Respondeu', type: 'date' },
        ],
        rfe_responded: [
          { key: 'rfe_date', label: 'Data que Recebeu RFE', type: 'date' },
          { key: 'rfe_deadline_date', label: 'Prazo de Resposta', type: 'date' },
          { key: 'rfe_response_date', label: 'Data que Respondeu', type: 'date' },
        ],
        noid_received: [
          { key: 'noid_date', label: 'Data que Recebeu NOID', type: 'date' },
          { key: 'noid_deadline_date', label: 'Prazo de Resposta', type: 'date' },
          { key: 'noid_response_date', label: 'Data que Respondeu', type: 'date' },
        ],
        noid_responded: [
          { key: 'noid_date', label: 'Data que Recebeu NOID', type: 'date' },
          { key: 'noid_deadline_date', label: 'Prazo de Resposta', type: 'date' },
          { key: 'noid_response_date', label: 'Data que Respondeu', type: 'date' },
        ],
        approved: [{ key: 'approval_date', label: 'Data Aprovação', type: 'date' }],
        denied: [{ key: 'denial_date', label: 'Data da Negativa', type: 'date' }],
      }
    );
  }

  return fields;
}

function buildStageFormSchema(fields: StageField[], requiredKeys: string[]) {
  const shape: Record<string, z.ZodTypeAny> = {};

  fields.forEach((field) => {
    const requiredMessage = `Preencha ${field.label.toLowerCase()}.`;

    if (field.type === 'date') {
      shape[field.key] = requiredKeys.includes(field.key)
        ? z.string().regex(DATE_VALUE_REGEX, requiredMessage)
        : z.union([z.literal(''), z.string().regex(DATE_VALUE_REGEX, 'Informe uma data válida.')]);
      return;
    }

    shape[field.key] = requiredKeys.includes(field.key)
      ? z.string().trim().min(1, requiredMessage)
      : z.string();
  });

  return z.object(shape);
}

// Toda etapa pode ter sua data reajustada: o status atual continua clicável.
function canEditCurrentStatusDates(_milestoneName: string, _status: string): boolean {
  return true;
}

function canEditCurrentStatusDatesLegacy(milestoneName: string, status: string): boolean {
  if (milestoneName === 'Envio (I-589)' && (status === 'sent' || status === 'receipt_received')) return true;
  // Allow re-editing PWD RFI to add response date
  if (isPwdMilestoneName(milestoneName) && (status === 'rfi' || status === 'preparing' || status === 'submitted')) return true;
  // Allow re-editing PERM stages
  if (isPermMilestoneName(milestoneName) && (status === 'rfi' || status === 'preparing' || status === 'submitted' || status === 'audit')) return true;
  // Allow re-editing I-140 stages
  if (hasPetitionDecision(milestoneName) && (status === 'preparing' || status === 'submitted' || status === 'rfe_received')) return true;
  return false;
}

function isLegacyI589Template(fields: StageField[]): boolean {
  const hasLegacyOfficial = fields.some((f) => f.key === 'receipt_date');
  const hasNewOfficial = fields.some((f) => f.key === 'official_filing_date');
  return hasLegacyOfficial && !hasNewOfficial;
}

function parseDateOnlyToTs(value?: string | null): number | null {
  if (!value) return null;
  const ts = Date.parse(`${value}T00:00:00Z`);
  return Number.isNaN(ts) ? null : ts;
}

/** Prazos abertos (RFE/NOID/RFI) com contagem regressiva. */
const DEADLINE_LABELS: Record<string, { label: string; responseKey: string }> = {
  rfe_deadline_date: { label: 'Prazo RFE', responseKey: 'rfe_response_date' },
  noid_deadline_date: { label: 'Prazo NOID', responseKey: 'noid_response_date' },
  rfi_due_date: { label: 'Prazo RFI', responseKey: 'rfi_response_date' },
};

function getOpenDeadlines(data: Record<string, any>) {
  return Object.entries(DEADLINE_LABELS)
    .filter(([key, cfg]) => !!data[key] && !data[cfg.responseKey])
    .map(([key, cfg]) => {
      const deadline = parseISO(String(data[key]));
      return {
        key,
        label: cfg.label,
        deadline,
        daysLeft: differenceInDays(deadline, new Date()),
      };
    })
    .filter((d) => !Number.isNaN(d.deadline.getTime()));
}

/** Relógio de 3 anos para ajuste de status (Visto T e Visto U). */
function getThreeYearClock(milestoneName: string, data: Record<string, any>) {
  if (!/ajuste de status|visa bulletin|\(3 anos\)/i.test(milestoneName)) return null;

  // Data de elegibilidade informada diretamente (Visto T / Visto U)
  const explicit = data.three_year_mark || data.eligibility_date;
  if (explicit) {
    const eligible = parseISO(String(explicit));
    if (!Number.isNaN(eligible.getTime())) {
      return { start: null as Date | null, eligible, daysLeft: differenceInDays(eligible, new Date()) };
    }
  }

  const base = data.approval_date || data.visa_approval_date || data.entered_date;
  if (!base) return null;
  const start = parseISO(String(base));
  if (Number.isNaN(start.getTime())) return null;
  const eligible = new Date(start);
  eligible.setFullYear(eligible.getFullYear() + 3);
  return { start, eligible, daysLeft: differenceInDays(eligible, new Date()) };
}

/**
 * Status que representam "protocolado/enviado" numa petição. A partir de
 * qualquer um deles a Decisão fica disponível.
 */
const FILING_STATUSES = new Set(['sent', 'filed', 'submitted', 'receipt_received']);
/** Datas que comprovam o protocolo, variando de template para template. */
const FILING_DATE_KEYS = [
  'filing_date',
  'official_filing_date',
  'sent_date',
  'receipt_date',
  'receipt_received_date',
  'submission_date',
];

function hasFilingDate(data: Record<string, any>): boolean {
  return FILING_DATE_KEYS.some((key) => !!data?.[key]);
}




export default function StageCard({
  milestone,
  index,
  onToggleComplete,
  onUpdateStatus,
  isUpdating,
  isLocked = false,
}: StageCardProps) {
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isPwdDecisionExpanded, setIsPwdDecisionExpanded] = useState(false);

  const category = (milestone.status_category || 'not_started') as StatusCategory;
  // Override: PWD milestones em pending devem mostrar "Ação Interna" (amarelo) no badge
  const displayCategory = ((isPwdMilestoneName(milestone.name) || isPermMilestoneName(milestone.name) || hasPetitionDecision(milestone.name)) && category === 'not_started')
    ? 'internal_action' as StatusCategory
    : category;
  const colors = STATUS_CATEGORY_COLORS[displayCategory];
  const isCompleted = category === 'completed';

  const statusFlow = milestone.template?.metadata?.status_flow || [];
  const fields = milestone.template?.metadata?.fields || [];
  const currentStatus = milestone.current_status || 'pending';
  const data = milestone.data || {};
  const openDeadlines = getOpenDeadlines(data);
  const threeYearClock = getThreeYearClock(milestone.name, data);


  const decisionConfig = getDecisionConfig(milestone.name);
  const visibleStatusFlow = (() => {
    if (!decisionConfig) return statusFlow;

    const baseFlow = statusFlow.filter((status) => !decisionConfig.decisionStatuses.has(status));
    const decisionPath = data.decision_path || data.pwd_decision_path; // backward compat

    // Determine which intermediate statuses the milestone went through
    const intermediateStatuses = decisionConfig.intermediateOptions.map(o => o.status);
    const activeIntermediate = intermediateStatuses.find(s => s === currentStatus || decisionPath === s);

    if (
      currentStatus === 'submitted' ||
      currentStatus === 'filed' ||
      (getDecisionConfig(milestone.name) && FILING_STATUSES.has(currentStatus))
    ) {
      return [...baseFlow, 'decision'];
    }

    // Check if current status is any intermediate or a follow-up (e.g. rfi_responded after rfi_received)
    const allDecisionNonFinal = [...decisionConfig.decisionStatuses].filter(
      s => !decisionConfig.finalOptions.some(f => f.status === s)
    );
    if (allDecisionNonFinal.includes(currentStatus)) {
      // Show the chain: e.g. rfi_received → rfi_responded
      const chain = allDecisionNonFinal.filter(s => {
        const idx = statusFlow.indexOf(s);
        const curIdx = statusFlow.indexOf(currentStatus);
        return idx >= 0 && idx <= curIdx;
      });
      // If chain is empty (status not in original flow), just show current
      const visibleChain = chain.length > 0 ? chain : [currentStatus];
      return [...baseFlow, ...visibleChain, 'decision'];
    }

    // Final statuses (approved, denied, certified)
    const finalStatuses = decisionConfig.finalOptions.map(o => o.status);
    if (finalStatuses.includes(currentStatus)) {
      const intermediateInPath = activeIntermediate ? [activeIntermediate] : [];
      return [...baseFlow, ...intermediateInPath, currentStatus];
    }

    return [...baseFlow, 'decision'];
  })();
  const currentFlowIdx = visibleStatusFlow.indexOf(currentStatus);
  const displayedFields = getDisplayFieldsForCurrentStatus(milestone.name, currentStatus, fields, data);
  const pendingStatusConfig = pendingStatus
    ? resolveStatusFieldConfig(milestone.name, pendingStatus, fields, data)
    : EMPTY_FIELD_CONFIG;
  const pendingStatusFields = pendingStatusConfig.visibleFields;
  const requiredPendingFieldKeys = pendingStatusConfig.requiredKeys;
  const hasMissingRequiredFields = requiredPendingFieldKeys.some((key) => !formData[key]);

  const allDecisionNonFinalSet = decisionConfig
    ? new Set([...decisionConfig.decisionStatuses].filter(s => !decisionConfig.finalOptions.some(f => f.status === s)))
    : new Set<string>();
  // "Protocolado" pode vir como submitted/filed/sent/receipt_received conforme o template.
  const isFiledStatus =
    currentStatus === 'submitted' ||
    (hasPetitionDecision(milestone.name) && FILING_STATUSES.has(currentStatus));
  const isRfiCompleted = currentStatus === 'rfi' && (milestone.status_category === 'completed' || milestone.status_category === 'external_wait');
  const isAuditCompleted = currentStatus === 'audit' && !!data?.audit_date;
  const isRfeCompleted =
    (currentStatus === 'rfe_received' || currentStatus === 'rfe_responded') && !!data?.rfe_response_date;
  const isNoidCompleted =
    (currentStatus === 'noid_received' || currentStatus === 'noid_responded') && !!data?.noid_response_date;
  const showDecisionSection = !!decisionConfig && (
    (isFiledStatus && (
      (!isPwdMilestoneName(milestone.name) && !isPermMilestoneName(milestone.name) && !hasPetitionDecision(milestone.name)) || hasFilingDate(data)
    )) ||
    // Etapas de "Aprovação" não têm protocolo próprio: a decisão fica sempre disponível.
    (hasPetitionDecision(milestone.name) && currentStatus === 'pending' && !statusFlow.includes('preparing') && !statusFlow.includes('filed') && !statusFlow.includes('submitted')) ||
    isRfiCompleted ||
    isAuditCompleted ||
    isRfeCompleted ||
    isNoidCompleted ||
    (allDecisionNonFinalSet.has(currentStatus) && !['rfi', 'audit', 'rfe_received', 'noid_received'].includes(currentStatus))
  );
  const showIntermediateInsideDecision =
    !!decisionConfig && (isFiledStatus || currentStatus === 'pending' || isRfeCompleted || isNoidCompleted);

  const handleStatusClick = (e: React.MouseEvent, status: string) => {
    e.stopPropagation();
    if (!onUpdateStatus) return;

    const statusConfig = resolveStatusFieldConfig(milestone.name, status, fields, data);

    if (statusConfig.visibleFields.length > 0 || isCompletionStatus(status)) {
      const initial: Record<string, string> = {};

      for (const field of statusConfig.visibleFields) {
        if (data[field.key]) initial[field.key] = data[field.key];
      }

      // Auto-fill preparation_start_date for PWD/PERM/I-140 Preparing phase 1
      if ((isPwdMilestoneName(milestone.name) || isPermMilestoneName(milestone.name) || hasPetitionDecision(milestone.name)) && status === 'preparing' && !data?.preparation_start_date) {
        initial.preparation_start_date = new Date().toISOString().split('T')[0];
      }

      if (milestone.name === 'Envio (I-589)' && status === 'sent') {
        if (!initial.sent_date && data.filing_date) initial.sent_date = data.filing_date;
      }

      if (milestone.name === 'Envio (I-589)' && status === 'receipt_received') {
        if (!initial.notice_received_date && data.notice_received_date) {
          initial.notice_received_date = data.notice_received_date;
        }
        if (!initial.official_filing_date) {
          if (data.official_filing_date) initial.official_filing_date = data.official_filing_date;
          else if (data.receipt_date) initial.official_filing_date = data.receipt_date;
        }
      }

      if (isCompletionStatus(status) && !statusConfig.visibleFields.some((field) => field.key === 'completed_date')) {
        // Permite ajustar a data: reaproveita a já registrada, senão sugere hoje.
        initial._completed_date =
          (currentStatus === status && milestone.completed_date
            ? milestone.completed_date.slice(0, 10)
            : null) ?? new Date().toISOString().split('T')[0];
      }

      setFormError(null);
      setFormData(initial);
      setPendingStatus(status);
      return;
    }

    setFormError(null);
    onUpdateStatus(milestone.id, status);
  };

  const handleConfirm = () => {
    if (!pendingStatus || !onUpdateStatus) return;

    const parsed = buildStageFormSchema(pendingStatusFields, requiredPendingFieldKeys).safeParse(
      pendingStatusFields.reduce<Record<string, string>>((acc, field) => {
        acc[field.key] = formData[field.key] || '';
        return acc;
      }, {})
    );

    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? 'Confira os dados informados.');
      return;
    }

    const extra: StatusUpdateExtra = {};
    const newData: Record<string, any> = { ...data };

    for (const [key, value] of Object.entries({ ...parsed.data, _completed_date: formData._completed_date })) {
      if (!value) continue;
      if (key === '_completed_date') {
        extra.completed_date = value;
      } else {
        newData[key] = value;
      }
    }

    if (Object.keys(newData).some((key) => newData[key] !== data[key])) {
      extra.data = newData;
    }

    // PWD Preparing: two-phase logic
    if (isPwdMilestoneName(milestone.name) && pendingStatus === 'preparing') {
      const hadStartDate = !!data?.preparation_start_date;
      if (!hadStartDate) {
        // Phase 1: Starting — save start date, set internal_action, stay in preparing
        extra.status_category = 'internal_action';
      } else if (newData.preparation_completed_date) {
        // Phase 2: Finalizing — auto-advance to submitted
        extra.data = newData;
        extra.status_category = 'internal_action';
        onUpdateStatus(milestone.id, 'submitted', extra);
        setPendingStatus(null);
        setFormError(null);
        return;
      }
    }

    // PWD RFI: override status_category based on filled fields and auto-advance
    if (isPwdMilestoneName(milestone.name) && pendingStatus === 'rfi') {
      const hasResponseDate = !!(newData.rfi_response_date);
      if (hasResponseDate) {
        // Response date filled → auto-advance back to decision (submitted)
        extra.data = newData;
        extra.status_category = 'external_wait';
        onUpdateStatus(milestone.id, 'rfi', extra);
        setPendingStatus(null);
        setFormData({});
        setFormError(null);
        return;
      }
      // Has received+due dates but no response → internal_action (team preparing response)
      const hasReceivedAndDue = !!(newData.rfi_received_date && newData.rfi_due_date);
      if (hasReceivedAndDue) {
        extra.status_category = 'internal_action';
      }
    }

    // PWD: após confirmar Protocolado com filing_date → external_wait (azul)
    if (isPwdMilestoneName(milestone.name) && pendingStatus === 'submitted' && newData.filing_date) {
      extra.status_category = 'external_wait';
    }

    // PERM Preparing: two-phase logic (same as PWD)
    if (isPermMilestoneName(milestone.name) && pendingStatus === 'preparing') {
      const hadStartDate = !!data?.preparation_start_date;
      if (!hadStartDate) {
        extra.status_category = 'internal_action';
      } else if (newData.preparation_completed_date) {
        extra.data = newData;
        extra.status_category = 'internal_action';
        onUpdateStatus(milestone.id, 'submitted', extra);
        setPendingStatus(null);
        setFormError(null);
        return;
      }
    }

    // PERM Submitted + filing_date → external_wait (azul)
    if (isPermMilestoneName(milestone.name) && pendingStatus === 'submitted' && newData.filing_date) {
      extra.status_category = 'external_wait';
    }

    // PERM RFI: same logic as PWD RFI
    if (isPermMilestoneName(milestone.name) && pendingStatus === 'rfi') {
      const hasResponseDate = !!(newData.rfi_response_date);
      if (hasResponseDate) {
        extra.data = newData;
        extra.status_category = 'external_wait';
        onUpdateStatus(milestone.id, 'rfi', extra);
        setPendingStatus(null);
        setFormData({});
        setFormError(null);
        return;
      }
      const hasReceivedAndDue = !!(newData.rfi_received_date && newData.rfi_due_date);
      if (hasReceivedAndDue) {
        extra.status_category = 'internal_action';
      }
    }

    // PERM Audit → internal_action
    if (isPermMilestoneName(milestone.name) && pendingStatus === 'audit') {
      extra.status_category = 'internal_action';
    }

    // I-140 Preparing: two-phase logic (same as PWD/PERM)
    if (hasPetitionDecision(milestone.name) && pendingStatus === 'preparing') {
      const hadStartDate = !!data?.preparation_start_date;
      if (!hadStartDate) {
        extra.status_category = 'internal_action';
      } else if (newData.preparation_completed_date) {
        extra.data = newData;
        extra.status_category = 'internal_action';
        onUpdateStatus(milestone.id, 'submitted', extra);
        setPendingStatus(null);
        setFormError(null);
        return;
      }
    }

    // RFE/NOID: duas fases (recebido → ação interna; respondido → aguardando USCIS)
    if (pendingStatus === 'rfe_received' || pendingStatus === 'noid_received') {
      const responseKey = pendingStatus === 'rfe_received' ? 'rfe_response_date' : 'noid_response_date';
      if (newData[responseKey]) {
        extra.data = newData;
        extra.status_category = 'external_wait';
        onUpdateStatus(milestone.id, pendingStatus, extra);
        setPendingStatus(null);
        setFormData({});
        setFormError(null);
        return;
      }
      // Só a data de recebimento → ação interna (âmbar)
      extra.status_category = 'internal_action';
    }

    // Protocolado/Enviado/Recibo → sempre aguardando decisão (azul).
    // Nunca "Concluído": a etapa só fecha com Aprovado/Negado.
    if (hasPetitionDecision(milestone.name) && FILING_STATUSES.has(pendingStatus)) {
      extra.status_category = 'external_wait';
    }


    // Desfechos mutuamente exclusivos (Aprovado x Negado, Concluído x Perdido):
    // ao escolher um, os dados do outro são apagados.
    const siblings = getExclusiveSiblings(pendingStatus);
    if (siblings.length > 0) {
      const cleaned = { ...(extra.data ?? newData) };
      let changed = false;
      for (const sibling of siblings) {
        for (const key of [`${sibling}_date`, ...(STATUS_DATE_REQUIRED_KEYS[sibling] ?? [])]) {
          if (key in cleaned) {
            delete cleaned[key];
            changed = true;
          }
        }
      }
      if (changed) extra.data = cleaned;
    }

    // Status não finalizador reabre a etapa (limpa a data de conclusão).
    if (!isCompletionStatus(pendingStatus) && milestone.completed_date) {
      extra.completed_date = null;
    }

    onUpdateStatus(milestone.id, pendingStatus, extra);
    setPendingStatus(null);
    setFormData({});
    setFormError(null);
  };

  const i589DateOrderError = (() => {
    if (!pendingStatus || milestone.name !== 'Envio (I-589)') return null;
    const legacyTemplate = isLegacyI589Template(fields);

    const sent =
      formData.sent_date ||
      formData.filing_date ||
      data.sent_date ||
      data.filing_date ||
      null;
    const official =
      (legacyTemplate
        ? (
            formData.receipt_date ||
            formData.official_filing_date ||
            data.receipt_date ||
            data.official_filing_date
          )
        : (
            formData.official_filing_date ||
            formData.receipt_date ||
            data.official_filing_date ||
            data.receipt_date
          )) ||
      null;
    const notice =
      formData.notice_received_date ||
      data.notice_received_date ||
      null;

    const sentTs = parseDateOnlyToTs(sent);
    const officialTs = parseDateOnlyToTs(official);
    const noticeTs = parseDateOnlyToTs(notice);

    if (sentTs && officialTs && sentTs > officialTs) {
      return 'Data de Envio não pode ser maior que Data Oficial USCIS.';
    }
    if (officialTs && noticeTs && officialTs > noticeTs) {
      return 'Data Oficial USCIS não pode ser maior que Data Recebida no Escritório.';
    }
    if (sentTs && noticeTs && sentTs > noticeTs) {
      return 'Data de Envio não pode ser maior que Data Recebida no Escritório.';
    }
    return null;
  })();

  const isConfirmDisabled = isUpdating || hasMissingRequiredFields || !!i589DateOrderError;

  return (
    <div
      className={`relative overflow-hidden border rounded-xl shadow-sm transition-all
        before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:rounded-l-xl
        ${isLocked
          ? 'before:bg-destructive border-destructive/40 ring-1 ring-destructive/30 bg-destructive/5 opacity-70 grayscale-[0.25] [&_button]:pointer-events-none [&_button]:opacity-60 [&_input]:pointer-events-none [&_select]:pointer-events-none'
          : `hover:shadow-md hover:-translate-y-px ${BORDER_LEFT_COLORS[category]} ${colors.border} ${isCompleted ? 'bg-emerald-50/60 dark:bg-emerald-950/20 ring-1 ring-emerald-500/30' : 'bg-card'}`}`}
    >
      {isLocked && (
        <div className="px-4 pt-3">
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-destructive/15 text-destructive">
            Cancelado
          </span>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-1">
        <div className="flex items-center gap-2.5">
          <div className={`relative w-[34px] h-[34px] rounded-lg flex items-center justify-center flex-shrink-0 ${ICON_BG[category]}`}>
            <MilestoneIcon icon={milestone.template?.icon} size={16} />
            {isCompleted && (
              <span className="absolute -right-1 -bottom-1 w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center">
                <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-sm font-semibold text-foreground truncate">{milestone.name}</span>
            {milestone.template?.metadata?.optional === true && (
              <span className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground flex-shrink-0">
                Opcional
              </span>
            )}
            {milestone.description && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="Informações da etapa"
                    className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground hover:text-foreground transition-colors shrink-0"
                  >
                    <Info className="w-3.5 h-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs whitespace-normal text-xs leading-relaxed">
                  {milestone.description}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
        {onUpdateStatus && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="Resetar etapa (temporário)"
                disabled={isUpdating}
                onClick={async () => {
                  if (!window.confirm(`Resetar a etapa "${milestone.name}"? Todos os dados e datas preenchidos serão apagados.`)) return;
                  await onUpdateStatus(milestone.id, 'pending', {
                    data: {},
                    completed_date: null,
                    status_category: 'not_started',
                    resetData: true,
                  });
                }}
                className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0 disabled:opacity-40"
              >
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="text-xs">Resetar etapa (temporário — apenas para testes)</TooltipContent>
          </Tooltip>
        )}
        {data['clock_eligible_date'] || data['clock_start_date'] ? (
          <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 border-0 text-[10px] font-semibold px-2.5 py-0.5 rounded-full">
            ⏳ Relógio de 150 Dias
          </Badge>
        ) : (
          <Badge className={`${colors.bg} ${colors.text} border-0 text-[10px] font-semibold px-2.5 py-0.5 rounded-full`}>
            {colors.label}
          </Badge>
        )}
        </div>
      </div>

      {/* Fields grid - always visible */}
      {displayedFields.length > 0 && (
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 px-4 pb-3">
          {displayedFields.map((field) => {
            const value = data[field.key];
            const displayLabel = milestone.name === 'Envio (I-589)' && (
              field.key === 'official_filing_date' || field.key === 'receipt_date'
            )
              ? 'Data do Filing'
              : field.label;
            return (
              <div key={field.key} className="flex flex-col gap-px">
                <span className="text-[9px] uppercase tracking-wider font-semibold text-muted-foreground">
                  {displayLabel}
                </span>
                <span className="text-xs font-medium text-foreground">
                  {value != null && value !== '' ? (
                    field.type === 'date' ? <DualDate dateStr={value} /> : String(value)
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* EAD clock computed fields */}
      {data['clock_eligible_date'] && (
        (() => {
          const eligible = parseISO(data['clock_eligible_date']);
          const today = new Date();
          const daysLeft = differenceInDays(eligible, today);
          const isPaused = currentStatus === 'clock_paused';
          const isEligible = currentStatus === 'eligible' || daysLeft <= 0;
          const clockLabel = isPaused ? 'Pausado' : isEligible ? 'Elegível' : 'Contando';
          const clockColor = isPaused
            ? 'text-amber-500'
            : isEligible
            ? 'text-emerald-500'
            : daysLeft <= 30
            ? 'text-rose-500'
            : 'text-blue-500';
          const daysColor = isPaused
            ? 'text-muted-foreground'
            : isEligible
            ? 'text-emerald-600 dark:text-emerald-400'
            : daysLeft <= 30
            ? 'text-rose-600 dark:text-rose-400'
            : 'text-foreground';
          return (
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 px-4 pb-3 border-t border-border/30 pt-2">
              <div className="flex flex-col gap-px">
                <span className="text-[9px] uppercase tracking-wider font-semibold text-muted-foreground">
                  Dias Restantes
                </span>
                <span className={`text-sm font-bold ${daysColor}`}>
                  {isPaused ? '—' : isEligible ? 'Elegível!' : `${daysLeft} dias`}
                </span>
              </div>
              <div className="flex flex-col gap-px">
                <span className="text-[9px] uppercase tracking-wider font-semibold text-muted-foreground">
                  Status do Relógio
                </span>
                <span className={`text-xs font-semibold flex items-center gap-1 ${clockColor}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-current inline-block" />
                  {clockLabel}
                </span>
              </div>
            </div>
          );
        })()
      )}

      {/* Prazos abertos (RFE / NOID / RFI) */}
      {openDeadlines.length > 0 && (
        <div className="px-4 pb-3 flex flex-wrap gap-2">
          {openDeadlines.map((d) => {
            const overdue = d.daysLeft < 0;
            const urgent = !overdue && d.daysLeft <= 15;
            const tone = overdue
              ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
              : urgent
              ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
              : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
            return (
              <Badge key={d.key} className={`${tone} border-0 text-[10px] font-semibold px-2.5 py-0.5 rounded-full`}>
                ⏰ {d.label}: {format(d.deadline, 'dd/MM/yyyy')} ·{' '}
                {overdue ? `${Math.abs(d.daysLeft)} dias em atraso` : `${d.daysLeft} dias restantes`}
              </Badge>
            );
          })}
        </div>
      )}

      {/* Relógio de 3 anos (ajuste de status — Visto T / Visto U) */}
      {threeYearClock && (
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 px-4 pb-3 border-t border-border/30 pt-2">
          <div className="flex flex-col gap-px">
            <span className="text-[9px] uppercase tracking-wider font-semibold text-muted-foreground">
              Elegível ao Ajuste (3 anos)
            </span>
            <span className="text-xs font-medium text-foreground">
              {format(threeYearClock.eligible, 'dd/MM/yyyy')}
            </span>
          </div>
          <div className="flex flex-col gap-px">
            <span className="text-[9px] uppercase tracking-wider font-semibold text-muted-foreground">
              Dias Restantes
            </span>
            <span
              className={`text-sm font-bold ${
                threeYearClock.daysLeft <= 0
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-foreground'
              }`}
            >
              {threeYearClock.daysLeft <= 0 ? 'Elegível!' : `${threeYearClock.daysLeft} dias`}
            </span>
          </div>
        </div>
      )}


      {/* Dates when no template fields */}
      {fields.length === 0 && (milestone.started_date || milestone.completed_date) && (
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 px-4 pb-3">
          {milestone.started_date && (
            <div className="flex flex-col gap-px">
              <span className="text-[9px] uppercase tracking-wider font-semibold text-muted-foreground">Início</span>
              <DualDate dateStr={milestone.started_date} />
            </div>
          )}
          {milestone.completed_date && (
            <div className="flex flex-col gap-px">
              <span className="text-[9px] uppercase tracking-wider font-semibold text-muted-foreground">Concluído</span>
              <DualDate dateStr={milestone.completed_date} />
            </div>
          )}
        </div>
      )}

      {/* Notes */}
      {milestone.notes && (
        <div className="mx-4 mb-3 text-xs text-muted-foreground bg-muted/50 p-2.5 rounded-lg">
          {milestone.notes}
        </div>
      )}

      {/* Clickable status flow bar */}
      {visibleStatusFlow.length > 0 && (
        <div className="border-t border-border/50 mx-4 pt-2.5 pb-3">
          <div className="flex flex-wrap gap-y-1.5">
            {visibleStatusFlow.map((status, i) => {
              const isFirst = i === 0;
              const isLast = i === visibleStatusFlow.length - 1;
              const arrow = 10;

              const clipPath = isFirst
                ? `polygon(0 0, calc(100% - ${arrow}px) 0, 100% 50%, calc(100% - ${arrow}px) 100%, 0 100%)`
                : isLast
                  ? `polygon(0 0, 100% 0, 100% 100%, 0 100%, ${arrow}px 50%)`
                  : `polygon(0 0, calc(100% - ${arrow}px) 0, 100% 50%, calc(100% - ${arrow}px) 100%, 0 100%, ${arrow}px 50%)`;

              if (status === 'decision') {
                const isFinal = decisionConfig?.finalOptions.some(o => o.status === currentStatus);
                const isDecisionLocked = !showDecisionSection && !isFinal;
                let decBg = 'bg-muted';
                let decText = 'text-muted-foreground';
                if (isFinal) {
                  const isFailed = currentStatus === 'denied' || milestone.status_category === 'failed';
                  decBg = isFailed ? 'bg-red-600' : 'bg-emerald-500';
                  decText = 'text-white';
                } else if (showDecisionSection) {
                  decBg = isPwdDecisionExpanded ? 'bg-blue-600' : 'bg-blue-500/60';
                  decText = isPwdDecisionExpanded ? 'text-white font-bold' : 'text-blue-900 dark:text-blue-200';
                }
                return (
                  <div
                    key={status}
                    style={{ clipPath, marginLeft: isFirst ? 0 : -(arrow - 2) }}
                    className={`flex-1 min-w-[80px] h-8 flex items-center justify-center transition-all ${isDecisionLocked ? 'cursor-default opacity-50' : 'cursor-pointer hover:brightness-110'} ${decBg}`}
                    onClick={() => {
                      if (!isDecisionLocked) setIsPwdDecisionExpanded((prev) => !prev);
                    }}
                  >
                    <span className={`text-[10px] font-semibold uppercase tracking-wide truncate ${decText}`} style={{ paddingLeft: isFirst ? 0 : arrow, paddingRight: isLast ? 0 : arrow }}>
                      {isFinal ? (currentStatus === 'denied' ? 'Negado' : currentStatus === 'certified' ? 'Certificado' : 'Aprovado') : 'Decisão'}
                    </span>
                  </div>
                );
              }

              // Só pinta como concluído o que realmente ficou para trás.
              // Status posteriores (ex.: "Recebido", "Perdido", "Negado") nunca
              // são preenchidos automaticamente.
              const isPast = i < currentFlowIdx;
              const isCurrent = i === currentFlowIdx && !isCompleted;

              let bgClass = 'bg-muted';
              let textClass = 'text-muted-foreground';

              // PWD/PERM-specific chevron overrides
              let pwdOverride = false;
              if (isPwdMilestoneName(milestone.name) || isPermMilestoneName(milestone.name) || hasPetitionDecision(milestone.name)) {
                if (status === 'pending' && data?.preparation_start_date) {
                  bgClass = 'bg-emerald-500';
                  textClass = 'text-white';
                  pwdOverride = true;
                } else if (status === 'preparing' && data?.preparation_completed_date) {
                  bgClass = 'bg-emerald-500';
                  textClass = 'text-white font-bold';
                  pwdOverride = true;
                } else if (FILING_STATUSES.has(status) && hasFilingDate(data)) {
                  bgClass = 'bg-emerald-500';
                  textClass = 'text-white';
                  pwdOverride = true;
                }
              }

              // Audit chevron: green when audit_date exists and moved past
              if (status === 'audit' && data?.audit_date) {
                bgClass = 'bg-emerald-500';
                textClass = 'text-white';
                pwdOverride = true;
              }

              // RFI chevron override: green when response date exists
              if (status === 'rfi' && data?.rfi_response_date) {
                bgClass = 'bg-emerald-500';
                textClass = 'text-white';
                pwdOverride = true;
              }

              // RFE/NOID chevron override: green when response date exists
              if (
                (status === 'rfe_received' && data?.rfe_response_date) ||
                (status === 'noid_received' && data?.noid_response_date)
              ) {
                bgClass = 'bg-emerald-500';
                textClass = 'text-white';
                pwdOverride = true;
              }

              if (!pwdOverride) {
                if (isPast) {
                  bgClass = 'bg-emerald-500';
                  textClass = 'text-white';
                } else if (isCurrent) {
                  if (status === 'preparing' && isPwdMilestoneName(milestone.name) && data?.preparation_completed_date) {
                    bgClass = 'bg-emerald-500';
                    textClass = 'text-white font-bold';
                  } else {
                    const cat = (milestone.status_category || 'internal_action') as string;
                    const currentBgMap: Record<string, string> = {
                      internal_action: 'bg-amber-400',
                      action_required: 'bg-rose-500',
                      external_wait: 'bg-blue-500',
                      completed: 'bg-emerald-500',
                      failed: 'bg-red-600',
                    };
                    bgClass = currentBgMap[cat] || 'bg-amber-400';
                    textClass = 'text-white font-bold';
                  }
                }
              }

              const isFutureSkip = i > currentFlowIdx + 1 && !isPast;
              const isDisabled = !onUpdateStatus || isUpdating || isFutureSkip || (currentStatus === status && !canEditCurrentStatusDates(milestone.name, status));
              const cursorClass = isDisabled ? `cursor-default ${isFutureSkip ? 'opacity-50' : ''}` : 'cursor-pointer hover:brightness-110';

              return (
                <div
                  key={status}
                  style={{ clipPath, marginLeft: isFirst ? 0 : -(arrow - 2) }}
                  className={`flex-1 min-w-[80px] h-8 flex items-center justify-center transition-all ${bgClass} ${cursorClass}`}
                  onClick={(e) => {
                    if (!isDisabled) handleStatusClick(e, status);
                  }}
                >
                  <span className={`text-[10px] font-semibold uppercase tracking-wide truncate ${textClass}`} style={{ paddingLeft: isFirst ? 0 : arrow, paddingRight: isLast ? 0 : arrow }}>
                    {translateStatus(status)}
                  </span>
                </div>
              );
            })}
          </div>

          {isPwdDecisionExpanded && decisionConfig && onUpdateStatus && !pendingStatus && (showDecisionSection || decisionConfig.finalOptions.some(o => o.status === currentStatus)) && (
            <div className="mt-2">
              <div className="p-3 rounded-lg border border-border bg-muted/30">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                  Decisão
                </p>
                <div className="flex gap-[2px]">
                  {showIntermediateInsideDecision && decisionConfig.intermediateOptions.map((opt) => (
                    <div
                      key={opt.status}
                      className="flex-1 h-8 rounded-md flex items-center justify-center px-1 bg-muted cursor-pointer hover:brightness-110 transition-all"
                      onClick={(e) => { if (!isUpdating) handleStatusClick(e, opt.status); }}
                    >
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground truncate">
                        {opt.label}
                      </span>
                    </div>
                  ))}
                  {decisionConfig.finalOptions.map((opt) => {
                    const isActive = currentStatus === opt.status;
                    return (
                      <div
                        key={opt.status}
                        className={`flex-1 h-8 rounded-md flex items-center justify-center px-1 transition-all cursor-pointer hover:brightness-110 ${
                          isActive ? (opt.status === 'denied' ? 'bg-red-600' : 'bg-emerald-500') : 'bg-muted'
                        }`}
                        onClick={(e) => { if (!isUpdating) handleStatusClick(e, opt.status); }}
                      >
                        <span className={`text-[10px] font-semibold uppercase tracking-wide truncate ${
                          isActive ? 'text-white' : 'text-muted-foreground'
                        }`}>
                          {opt.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {pendingStatus && (
            <div
              className="mt-3 p-3 rounded-lg border border-primary/30 bg-primary/5"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                  <CalendarDays className="w-3.5 h-3.5 text-primary" />
                  {translateStatus(pendingStatus)}
                </div>
                <button
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    setPendingStatus(null);
                    setFormError(null);
                  }}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>

              <div className="space-y-2">
                {pendingStatusFields.map((field) => {
                  const isRequired = requiredPendingFieldKeys.includes(field.key);
                  const fieldLabel = milestone.name === 'Envio (I-589)' && (
                    field.key === 'official_filing_date' || field.key === 'receipt_date'
                  ) ? 'Data do Filing' : field.label;

                  return (
                    <div key={field.key}>
                      <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground block mb-1">
                        {fieldLabel}
                        {!isRequired && <span className="ml-1 text-[9px] font-normal normal-case">(opcional)</span>}
                      </label>

                      {field.type === 'date' ? (
                        <DateInputBR
                          value={formData[field.key] || ''}
                          onChange={(val) => {
                            setFormError(null);
                            setFormData((prev) => ({ ...prev, [field.key]: val }));
                          }}
                          clearable={!isRequired}
                        />
                      ) : field.type === 'select' ? (
                        <select
                          value={formData[field.key] || ''}
                          onChange={(e) => {
                            setFormError(null);
                            setFormData((prev) => ({ ...prev, [field.key]: e.target.value }));
                          }}
                          className="w-full text-sm border border-border rounded-md px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                        >
                          <option value="">Selecione...</option>
                          {field.options?.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      ) : field.currency ? (
                        <CurrencyInput
                          value={formData[field.key] || ''}
                          onChange={(val) => {
                            setFormError(null);
                            setFormData((prev) => ({ ...prev, [field.key]: val }));
                          }}
                        />
                      ) : field.mask ? (
                        <MaskedTextInput
                          mask={field.mask}
                          value={formData[field.key] || ''}
                          onChange={(val) => {
                            setFormError(null);
                            setFormData((prev) => ({ ...prev, [field.key]: val }));
                          }}
                        />
                      ) : (
                        <input
                          type="text"
                          value={formData[field.key] || ''}
                          onChange={(e) => {
                            setFormError(null);
                            setFormData((prev) => ({ ...prev, [field.key]: e.target.value }));
                          }}
                          placeholder={
                            field.key === 'appointment_time'
                              ? 'HH:mm'
                              : (field.key === 'location' || field.key.includes('address'))
                                ? '123 Main St, Orlando, FL 32837'
                                : ''
                          }
                          className="w-full text-sm border border-border rounded-md px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      )}
                    </div>
                  );
                })}

                {isCompletionStatus(pendingStatus) && !pendingStatusFields.some((field) => field.key === 'completed_date') && (
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground block mb-1">
                      Data de conclusão
                    </label>
                    <DateInputBR
                      value={formData._completed_date || ''}
                      onChange={(val) => {
                        setFormError(null);
                        setFormData((prev) => ({ ...prev, _completed_date: val }));
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-2 mt-3">
                <Button
                  size="sm"
                  className="h-7 text-xs flex-1"
                  disabled={isConfirmDisabled}
                  onClick={handleConfirm}
                >
                  {isUpdating ? <Loader2 className="w-3 h-3 animate-spin" /> : (
                    (isPwdMilestoneName(milestone.name) || isPermMilestoneName(milestone.name))
                      ? (pendingStatus === 'preparing'
                          ? (!data?.preparation_start_date ? 'Iniciar' : 'Finalizar')
                          : pendingStatus === 'submitted'
                            ? 'Protocolar'
                            : 'Confirmar')
                      : 'Confirmar'
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs"
                  onClick={() => {
                    setPendingStatus(null);
                    setFormError(null);
                  }}
                >
                  Cancelar
                </Button>
              </div>
              {hasMissingRequiredFields && (
                <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-2">
                  Preencha todos os campos obrigatórios para este status.
                </p>
              )}
              {formError && (
                <p className="text-[10px] text-rose-600 dark:text-rose-400 mt-2">
                  {formError}
                </p>
              )}
              {i589DateOrderError && (
                <p className="text-[10px] text-rose-600 dark:text-rose-400 mt-2">
                  {i589DateOrderError}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Quick toggle when no flow */}
      {statusFlow.length === 0 && (
        <div className="px-4 pb-3">
          <Button
            variant={isCompleted ? 'outline' : 'default'}
            size="sm"
            className="text-xs h-8"
            onClick={(e) => {
              e.stopPropagation();
              onToggleComplete(milestone);
            }}
            disabled={isUpdating}
          >
            {isUpdating ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : isCompleted ? (
              <>
                <Circle className="w-3.5 h-3.5 mr-1.5" />
                Marcar Pendente
              </>
            ) : (
              <>
                <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                Marcar Concluído
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
